import { toast } from "sonner";

export type NormalizedApiError = {
  code: string;
  message: string;
  description?: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object");
}

export function normalizeApiError(value: unknown): NormalizedApiError {
  if (value instanceof Error) {
    return { code: "REQUEST_ERROR", message: value.message };
  }

  if (typeof value === "string") {
    const text = value.trim();
    if (/504 Gateway Time-out/i.test(text)) {
      return { code: "GATEWAY_TIMEOUT", message: "接口请求超时：服务器检测任务耗时过长，请稍后重试或先检查服务器/宝塔连接参数。" };
    }
    if (/^\s*</.test(text)) {
      return { code: "INVALID_RESPONSE", message: "接口没有返回 JSON，而是返回了 HTML 页面，请检查 API 地址、反向代理或登录状态。" };
    }
    return { code: "REQUEST_ERROR", message: value };
  }

  if (isRecord(value)) {
    const nestedError = value.error;
    if (isRecord(nestedError)) {
      return formatKnownApiError({
        code: typeof nestedError.code === "string" ? nestedError.code : "REQUEST_ERROR",
        message: typeof nestedError.message === "string" ? nestedError.message : "Request failed"
      });
    }

    return formatKnownApiError({
      code: typeof value.code === "string" ? value.code : "REQUEST_ERROR",
      message: typeof value.message === "string" ? value.message : "Request failed"
    });
  }

  return { code: "REQUEST_ERROR", message: "Request failed" };
}

function formatKnownApiError(error: NormalizedApiError): NormalizedApiError {
  if (error.code === "WP_AUTH_FAILED") {
    return {
      code: error.code,
      message: "WordPress 应用密码授权失败",
      description: "这里需要填写 Application Password，不是 /wp-admin 登录密码。请在 WordPress 后台重新生成应用密码，或使用“重置 WordPress 登录密码”自动生成新的应用密码。"
    };
  }

  return error;
}

export async function readResponseBody(response: Response) {
  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export async function notifyApiError(response: Response, fallbackMessage = "Request failed") {
  const body = await response.clone().text();
  const parsedBody = body
    ? (() => {
        try {
          return JSON.parse(body) as unknown;
        } catch {
          return body;
        }
      })()
    : { message: fallbackMessage };
  const error = normalizeApiError(parsedBody);
  const details = [
    error.code,
    `HTTP ${response.status}`,
    response.headers.get("content-type") || "",
    response.url
  ].filter(Boolean).join(" · ");

  toast.error(error.message, {
    description: error.description || details
  });
}
